import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings2,
  Palette,
  ImagePlus,
  LayoutTemplate,
  CheckCircle,
  AlertCircle,
  Loader2,
  ImageOff,
  Upload,
  Check,
  Save,
} from 'lucide-react';
import api from '../api/api';
import styles from './SettingsPage.module.css';

// ─────────────────────────────────────────────────────────────
// SOUS-COMPOSANTS UTILITAIRES
// ─────────────────────────────────────────────────────────────

const Alert = ({ message, type }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div className={`${styles.alert} ${isSuccess ? styles.alertSuccess : styles.alertError}`}>
      {isSuccess
        ? <CheckCircle size={16} strokeWidth={2} />
        : <AlertCircle size={16} strokeWidth={2} />}
      {message}
    </div>
  );
};

const Spinner = () => <span className={styles.spinner} aria-hidden="true" />;

// ─────────────────────────────────────────────────────────────
// ONGLETS
// ─────────────────────────────────────────────────────────────
const TABS = [
  { id: 'config', Icon: Palette,        label: 'Configuration' },
  { id: 'upload', Icon: ImagePlus,      label: 'Logos'         },
  { id: 'assets', Icon: LayoutTemplate, label: 'Assets'        },
];

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const SettingsPage = () => {
  const [activeTab,          setActiveTab]          = useState('config');
  const [settings,           setSettings]           = useState(null);
  const [initialLoading,     setInitialLoading]     = useState(true);
  const [feedback,           setFeedback]           = useState({ message: '', type: '' });
  const [isChangingActive,   setIsChangingActive]   = useState(false);
  const [isSavingColors,     setIsSavingColors]     = useState(null);
  const [isUploadingLogo,    setIsUploadingLogo]    = useState(false);
  const [isSavingAsset,      setIsSavingAsset]      = useState(false);
  const [logoFile,           setLogoFile]           = useState(null);
  const [logoPreview,        setLogoPreview]        = useState(null);
  const [logoTemplateTarget, setLogoTemplateTarget] = useState('Template1');

  // ── Feedback auto-dismiss ──────────────────────────────────
  useEffect(() => {
    if (!feedback.message) return;
    const t = setTimeout(() => setFeedback({ message: '', type: '' }), 5000);
    return () => clearTimeout(t);
  }, [feedback.message]);

  // ── Revoke preview URL ────────────────────────────────────
  useEffect(() => () => { if (logoPreview) URL.revokeObjectURL(logoPreview); }, [logoPreview]);

  // ── Fetch settings ────────────────────────────────────────
  const fetchSettings = useCallback(async (showLoader = true) => {
    if (showLoader) setInitialLoading(true);
    try {
      const { data } = await api.get('/settings/templates');
      setSettings(data);
    } catch {
      setFeedback({ message: 'Impossible de charger les paramètres.', type: 'error' });
    } finally {
      if (showLoader) setInitialLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // ── Handlers ──────────────────────────────────────────────
  const handleColorChange = (templateName, idx, value) =>
    setSettings(prev => ({
      ...prev,
      templates: {
        ...prev.templates,
        [templateName]: { ...prev.templates[templateName], [`color_hex_${idx}`]: value },
      },
    }));

  const handleActiveTemplateChange = async (e) => {
    const name = e.target.value;
    setIsChangingActive(true);
    try {
      await api.put('/settings/templates/active', { template_name: name });
      setSettings(prev => ({ ...prev, active_template: name }));
      setFeedback({ message: `${name} est maintenant le template actif.`, type: 'success' });
    } catch {
      setFeedback({ message: 'Erreur lors du changement de template.', type: 'error' });
    } finally {
      setIsChangingActive(false);
    }
  };

  const handleSaveColors = async (templateName) => {
    setIsSavingColors(templateName);
    try {
      await api.put(`/settings/templates/${templateName}`, settings.templates[templateName]);
      setFeedback({ message: `Couleurs du ${templateName} enregistrées.`, type: 'success' });
    } catch {
      setFeedback({ message: `Erreur lors de la sauvegarde pour ${templateName}.`, type: 'error' });
    } finally {
      setIsSavingColors(null);
    }
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file ?? null);
    setLogoPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleLogoUpload = async (e) => {
    e.preventDefault();
    if (!logoFile) {
      setFeedback({ message: 'Veuillez sélectionner un fichier.', type: 'error' });
      return;
    }
    const formData = new FormData();
    formData.append('logo_upload', logoFile);
    setIsUploadingLogo(true);
    try {
      const { data } = await api.post(
        `/settings/templates/${logoTemplateTarget}/logo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const newUrl = data?.newLogoUrl || data?.logo || data?.url;
      if (newUrl) {
        setSettings(prev => ({
          ...prev,
          templates: {
            ...prev.templates,
            [logoTemplateTarget]: { ...prev.templates[logoTemplateTarget], logo: newUrl },
          },
        }));
      } else {
        await fetchSettings(false);
      }
      setFeedback({ message: `Logo du ${logoTemplateTarget} mis à jour.`, type: 'success' });
      setLogoFile(null);
      setLogoPreview(null);
      e.target.reset();
    } catch {
      setFeedback({ message: "Erreur lors de l'envoi du logo.", type: 'error' });
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSaveAsset = async (assetName) => {
    setIsSavingAsset(assetName);
    try {
      await api.put('/settings/assets/active', { asset_name: assetName });
      setSettings(prev => ({ ...prev, active_asset: assetName }));
      setFeedback({ message: `Layout ${assetName} appliqué avec succès.`, type: 'success' });
    } catch {
      setFeedback({ message: 'Erreur lors de la sauvegarde du layout.', type: 'error' });
    } finally {
      setIsSavingAsset(false);
    }
  };

  // ── États de chargement / erreur ──────────────────────────
  if (initialLoading) {
    return (
      <div className={styles.loaderWrap}>
        <span className={styles.loaderSpinner} />
        <span className={styles.loaderText}>Chargement des paramètres…</span>
      </div>
    );
  }
  if (!settings) {
    return <Alert message="Aucun paramètre trouvé ou erreur de chargement." type="error" />;
  }

  return (
    <div className={styles.layout}>

      {/* ── En-tête ── */}
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.headerIcon}>
            <Settings2 size={22} strokeWidth={1.75} />
          </div>
          <div className={styles.headerText}>
            <h1>Configuration des Templates</h1>
            <p>Personnalisez l'apparence et le contenu de vos templates</p>
          </div>
        </div>
      </header>

      {/* ── Navigation par onglets ── */}
      <nav className={styles.tabNav} aria-label="Onglets des paramètres">
        {TABS.map(({ id, Icon, label }) => (
          <button
            key={id}
            className={`${styles.tabBtn} ${activeTab === id ? styles.tabBtnActive : ''}`}
            onClick={() => setActiveTab(id)}
            aria-current={activeTab === id ? 'page' : undefined}
          >
            <Icon size={15} strokeWidth={2} />
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {/* ── Feedback global ── */}
      <Alert message={feedback.message} type={feedback.type} />

      {/* ══════════════════════════════════════════════════════
          ONGLET — CONFIGURATION
      ══════════════════════════════════════════════════════ */}
      {activeTab === 'config' && (
        <div className={styles.tabPane}>

          {/* Template actif */}
          <section className={styles.sectionCard}>
            <div className={styles.sectionHead}>
              <h2><Palette size={18} strokeWidth={2} /> Template Actif</h2>
              <p>Sélectionnez le template utilisé pour la génération des documents</p>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.templateGrid}>
                {['Template1', 'Template2', 'Template3'].map((name) => {
                  const tpl = settings.templates[name];
                  if (!tpl) return null;
                  const isActive = settings.active_template === name;
                  return (
                    <label
                      key={name}
                      className={`${styles.templateCard} ${isActive ? styles.templateCardActive : ''}`}
                    >
                      <input
                        type="radio"
                        name="active_template"
                        value={name}
                        checked={isActive}
                        onChange={handleActiveTemplateChange}
                        disabled={isChangingActive}
                        className={styles.radioHidden}
                      />

                      {/* Badge */}
                      <span className={`${styles.templateBadge} ${isActive ? styles.templateBadgeActive : styles.templateBadgeInactive}`}>
                        {isActive && <span className={styles.pulseDot} />}
                        {isActive ? 'Actif' : 'Inactif'}
                      </span>

                      <h3 className={styles.templateCardTitle}>{name}</h3>

                      {/* Aperçu logo */}
                      <div className={styles.templateLogoBox}>
                        {tpl.logo
                          ? <img src={tpl.logo} alt={`Logo ${name}`} />
                          : (
                            <div className={styles.noLogo}>
                              <ImageOff size={22} strokeWidth={1.5} />
                              <span>Aucun logo</span>
                            </div>
                          )
                        }
                      </div>

                      {/* Palette */}
                      <div className={styles.colorPalette}>
                        {[tpl.color_hex_1, tpl.color_hex_2, tpl.color_hex_3].map((hex, i) => (
                          <span
                            key={i}
                            className={styles.colorSwatch}
                            style={{ backgroundColor: hex || '#e5e7eb' }}
                            title={hex}
                          />
                        ))}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Personnalisation des couleurs */}
          <section className={styles.sectionCard}>
            <div className={styles.sectionHead}>
              <h2><Palette size={18} strokeWidth={2} /> Personnalisation des Couleurs</h2>
              <p>Modifiez la palette de couleurs pour chaque template</p>
            </div>
            <div className={styles.sectionBody}>
              <div className={styles.colorGrid}>
                {Object.keys(settings.templates).map((templateName) => {
                  const tpl = settings.templates[templateName];
                  const saving = isSavingColors === templateName;
                  return (
                    <div key={templateName} className={styles.colorCard}>
                      <div className={styles.colorCardHead}>
                        <h3>{templateName}</h3>
                        <div className={styles.colorMiniPalette}>
                          {[tpl.color_hex_1, tpl.color_hex_2, tpl.color_hex_3].map((hex, i) => (
                            <span key={i} className={styles.colorMiniSwatch} style={{ backgroundColor: hex }} />
                          ))}
                        </div>
                      </div>

                      <div className={styles.colorPickerList}>
                        {[
                          { idx: 1, label: 'Principale',  fallback: '#0d4a68' },
                          { idx: 2, label: 'Secondaire',  fallback: '#2e9fd4' },
                          { idx: 3, label: 'Tertiaire',   fallback: '#e8f4f8' },
                        ].map(({ idx, label, fallback }) => (
                          <div key={idx} className={styles.colorInputGroup}>
                            <span className={styles.colorInputLabel}>{label}</span>
                            <div className={styles.colorInputRow}>
                              <input
                                type="color"
                                className={styles.colorInput}
                                value={tpl[`color_hex_${idx}`] || fallback}
                                onChange={(e) => handleColorChange(templateName, idx, e.target.value)}
                              />
                              <span className={styles.colorHex}>
                                {tpl[`color_hex_${idx}`] || fallback}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        className={`${styles.btnPrimary} ${saving ? styles.loading : ''}`}
                        onClick={() => handleSaveColors(templateName)}
                        disabled={saving}
                      >
                        {saving ? <><Spinner /> Sauvegarde…</> : <><Save size={14} strokeWidth={2} /> Sauvegarder</>}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          ONGLET — LOGOS
      ══════════════════════════════════════════════════════ */}
      {activeTab === 'upload' && (
        <div className={styles.tabPane}>
          <section className={styles.sectionCard}>
            <div className={styles.sectionHead}>
              <h2><ImagePlus size={18} strokeWidth={2} /> Gestion des Logos</h2>
              <p>Téléchargez et gérez les logos pour chaque template</p>
            </div>
            <div className={styles.sectionBody}>
              <form onSubmit={handleLogoUpload} className={styles.uploadForm}>

                <div className={styles.formGrid}>
                  {/* Sélecteur de template */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel} htmlFor="logo-template-select">
                      Template cible
                    </label>
                    <div className={styles.selectWrap}>
                      <select
                        id="logo-template-select"
                        className={styles.select}
                        value={logoTemplateTarget}
                        onChange={(e) => setLogoTemplateTarget(e.target.value)}
                      >
                        <option value="Template1">Template 1</option>
                        <option value="Template2">Template 2</option>
                        <option value="Template3">Template 3</option>
                      </select>
                    </div>
                  </div>

                  {/* Zone de dépôt */}
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Nouveau logo</label>
                    <input
                      type="file"
                      accept="image/*"
                      id="logo-file-input"
                      className={styles.fileInputHidden}
                      onChange={handleLogoFileChange}
                    />
                    <label htmlFor="logo-file-input" className={styles.fileDropZone}>
                      <div className={styles.fileDropIcon}>
                        <Upload size={20} strokeWidth={1.75} />
                      </div>
                      <div className={styles.fileDropText}>
                        <strong>
                          {logoFile ? logoFile.name : 'Choisir un fichier'}
                        </strong>
                        <small>PNG, JPG, SVG · Max 5 MB</small>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Aperçus */}
                {(settings.templates[logoTemplateTarget]?.logo || logoPreview) && (
                  <div className={styles.logoPreviews}>
                    {settings.templates[logoTemplateTarget]?.logo && (
                      <div className={styles.logoPreviewCard}>
                        <h4>Logo actuel</h4>
                        <div className={styles.logoDisplay}>
                          <img src={settings.templates[logoTemplateTarget].logo} alt="Logo actuel" />
                        </div>
                      </div>
                    )}
                    {logoPreview && (
                      <div className={styles.logoPreviewCard}>
                        <h4>Aperçu nouveau logo</h4>
                        <div className={styles.logoDisplay}>
                          <img src={logoPreview} alt="Aperçu" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className={`${styles.btnPrimary} ${isUploadingLogo ? styles.loading : ''}`}
                  disabled={isUploadingLogo || !logoFile}
                >
                  {isUploadingLogo
                    ? <><Spinner /> Envoi en cours…</>
                    : <><Upload size={14} strokeWidth={2} /> Mettre à jour le logo</>}
                </button>
              </form>
            </div>
          </section>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════
          ONGLET — ASSETS
      ══════════════════════════════════════════════════════ */}
      {activeTab === 'assets' && (
        <div className={styles.tabPane}>
          <section className={styles.sectionCard}>
            <div className={styles.sectionHead}>
              <h2><LayoutTemplate size={18} strokeWidth={2} /> Configuration des Assets</h2>
              <p>Sélectionnez le layout pour l'affichage des missions détaillées</p>
              <div className={styles.currentAssetBadge}>
                <Check size={12} strokeWidth={2.5} />
                Layout actuel : <strong>{settings.active_asset || 'Asset1'}</strong>
              </div>
            </div>

            <div className={styles.sectionBody}>
              <div className={styles.assetsGrid}>
                {ASSET_CONFIGS.map(({ id, title, desc, Preview }) => {
                  const isSelected = settings.active_asset === id;
                  const saving = isSavingAsset === id;
                  return (
                    <div
                      key={id}
                      className={`${styles.assetCard} ${isSelected ? styles.assetCardSelected : ''}`}
                    >
                      <div className={styles.assetPreview}>
                        <Preview />
                      </div>
                      <div className={styles.assetInfo}>
                        <h3>{title}</h3>
                        <p>{desc}</p>
                        <button
                          className={`${styles.btnPrimary} ${saving ? styles.loading : ''} ${isSelected ? styles.btnActive : ''}`}
                          onClick={() => !isSelected && handleSaveAsset(id)}
                          disabled={saving || isSelected}
                        >
                          {saving
                            ? <><Spinner /> Application…</>
                            : isSelected
                            ? <><Check size={14} strokeWidth={2.5} /> Layout Actif</>
                            : 'Appliquer ce layout'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PREVIEWS DES LAYOUTS (composants purs, pas de state)
// ─────────────────────────────────────────────────────────────
const Layout1Preview = () => (
  <div className={styles.layoutPreview}>
    <div className={styles.lHeader}>EN-TÊTE</div>
    <div className={styles.lBody}>
      <div className={`${styles.lCol} ${styles.lColLeft}`}>
        <div className={`${styles.lBlock} ${styles.lBlockRealisation}`}>RÉALISATIONS</div>
      </div>
      <div className={`${styles.lCol} ${styles.lColRight}`}>
        <div className={`${styles.lBlock} ${styles.lBlockContexte}`}>CONTEXTE</div>
        <div className={`${styles.lBlock} ${styles.lBlockTechnique}`}>TECHNIQUE</div>
      </div>
    </div>
    <div className={styles.lFooter}>PIED-DE-PAGE</div>
  </div>
);

const Layout2Preview = () => (
  <div className={styles.layoutPreview}>
    <div className={styles.lHeader}>EN-TÊTE</div>
    <div className={`${styles.lBlockFull} ${styles.lBlockContexte2}`}>CONTEXTE</div>
    <div className={styles.lSectionTitle}>RÉALISATIONS</div>
    <div className={styles.lBody}>
      <div className={`${styles.lCol} ${styles.lColLeft}`}>
        <div className={`${styles.lBlock} ${styles.lBlockRealisation2}`}>RÉALISATIONS</div>
      </div>
      <div className={`${styles.lCol} ${styles.lColRight}`}>
        <div className={`${styles.lBlock} ${styles.lBlockTechnique2}`}>TECHNIQUE</div>
      </div>
    </div>
    <div className={styles.lFooter}>PIED-DE-PAGE</div>
  </div>
);

const Layout3Preview = () => (
  <div className={styles.layoutPreview}>
    <div className={styles.lHeader}>EN-TÊTE</div>
    <div className={styles.lBody} style={{ flex: 'none', height: 40 }}>
      <div className={`${styles.lCol} ${styles.lColLeft}`}>
        <div className={`${styles.lBlock} ${styles.lBlockContexte3}`}>CONTEXTE</div>
      </div>
      <div className={`${styles.lCol} ${styles.lColRight}`}>
        <div className={`${styles.lBlock} ${styles.lBlockTechnique3}`}>TECHNIQUE</div>
      </div>
    </div>
    <div className={styles.lSectionTitle}>RÉALISATIONS</div>
    <div className={`${styles.lBlockFull} ${styles.lBlockRealisation3}`}>RÉALISATIONS</div>
    <div className={styles.lFooter}>PIED-DE-PAGE</div>
  </div>
);

const Layout4Preview = () => (
  <div className={styles.layoutPreview}>
    <div className={styles.lHeader}>EN-TÊTE</div>
    <div className={`${styles.lBlockFull} ${styles.lBlockContexte4}`}>CONTEXTE</div>
    <div className={`${styles.lBlockFull} ${styles.lBlockTechnique4}`}>ENVIRONNEMENT TECHNIQUE</div>
    <div className={styles.lSectionTitle}>RÉALISATIONS</div>
    <div className={`${styles.lBlockFull} ${styles.lBlockRealisation4}`}>RÉALISATIONS</div>
    <div className={styles.lFooter}>PIED-DE-PAGE</div>
  </div>
);

const ASSET_CONFIGS = [
  {
    id: 'Asset1',
    title: 'Layout 1',
    desc: 'Réalisations à gauche, Contexte et Technique à droite',
    Preview: Layout1Preview,
  },
  {
    id: 'Asset2',
    title: 'Layout 2',
    desc: 'Contexte plein largeur, Réalisations et Technique côte à côte',
    Preview: Layout2Preview,
  },
  {
    id: 'Asset3',
    title: 'Layout 3',
    desc: 'Contexte et Technique en haut, Réalisations pleine largeur',
    Preview: Layout3Preview,
  },
  {
    id: 'Asset4',
    title: 'Layout 4',
    desc: 'Layout vertical avec sections pleine largeur',
    Preview: Layout4Preview,
  },
];

export default SettingsPage;